import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { GraduationCap, ShieldCheck, ArrowUpRight } from "lucide-react";
import CoeLabCardLink from "../components/CoeLabCardLink";

export default function StudentLMS() {
  const [labs, setLabs] = useState([]);
  const [enrolls, setEnrolls] = useState([]);
  const [tab, setTab] = useState("active"); // active | browse

  const load = useCallback(async () => {
    const r = await api.get("/lms/labs");
    setLabs(r.data);
    const r2 = await api.get("/me/lms/enrollments");
    setEnrolls(r2.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const enrolledIds = new Set(enrolls.map(e => e.lab_id));
  const browseLabs = labs.filter(l => !enrolledIds.has(l.id));

  return (
    <div className="space-y-8">
      <div className="card card-pad bg-brand-gradient text-white">
        <span className="eyebrow on-grad-muted">Centers of Excellence</span>
        <h2 className="font-display font-extrabold text-3xl md:text-4xl mt-2">Train where industry trains.</h2>
        <p className="on-grad-muted mt-2 max-w-xl">Pick a lab, finish modules, pass quizzes — earn a verifiable certificate and a CoE badge that recruiters see on your profile.</p>
        <div className="mt-4 flex gap-3">
          <Link to="/student/lms/certificates" className="btn-ghost-dark justify-center" data-testid="my-certs-link">
            <ShieldCheck size={14} /> My Certificates
          </Link>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-rule">
        {[["active", `My Labs (${enrolls.length})`], ["browse", `Browse (${browseLabs.length})`]].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`lms-tab-${k}`}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${tab === k ? "border-signal text-signal" : "border-transparent text-muted hover:text-ink"}`}>
            {lbl}
          </button>
        ))}
      </div>

      {tab === "active" && (
        enrolls.length === 0 ? (
          <div className="card card-pad text-muted" data-testid="no-enrolls">
            You haven&apos;t enrolled in any lab yet. Go to <button onClick={() => setTab("browse")} className="text-signal font-semibold link-underline">Browse</button>.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {enrolls.map(e => (
              <CoeLabCardLink key={e.id}
                              labCode={e.lab_code}
                              fallbackInternalHref={`/lms/labs/${e.lab_id}`}
                              className="card card-pad card-hover block"
                              testid={`enr-${e.lab_id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs px-2 py-1 rounded-sm"
                    style={{ background: `${e.lab_color}15`, color: e.lab_color, border: `1px solid ${e.lab_color}40` }}>
                    {e.lab_code}
                  </span>
                  <span className={`badge ${e.status === "completed" ? "badge-signal" : ""}`}>{e.status}</span>
                </div>
                <h3 className="font-display font-bold text-lg mt-3">{e.lab_name}</h3>
                <div className="mt-4">
                  <div className="h-2 bg-rule rounded-sm overflow-hidden">
                    <div className="h-full" style={{ width: `${e.progress_pct}%`, background: e.lab_color }} />
                  </div>
                  <div className="text-xs text-muted mt-1">{e.progress_pct}% complete</div>
                </div>
                <div className="mt-4 inline-flex items-center gap-1 text-signal font-semibold text-sm">
                  Continue <ArrowUpRight size={14} />
                </div>
              </CoeLabCardLink>
            ))}
          </div>
        )
      )}

      {tab === "browse" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {browseLabs.map(l => (
            <CoeLabCardLink key={l.id}
                            labCode={l.code}
                            fallbackInternalHref={`/lms/labs/${l.id}`}
                            className="card overflow-hidden card-hover block"
                            testid={`browse-${l.code}`}>
              {l.cover_image ? (
                <div className="relative h-32 overflow-hidden">
                  <img src={l.cover_image} alt={l.name} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${l.color}00 50%, ${l.color}cc 100%)` }} />
                  <span className="absolute top-3 left-3 font-mono text-xs px-2 py-1 rounded-sm bg-white/95" style={{ color: l.color, border: `1px solid ${l.color}40` }}>{l.code}</span>
                </div>
              ) : (
                <div className="h-2" style={{ background: l.color }} />
              )}
              <div className="card-pad">
                {!l.cover_image && (
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs px-2 py-1 rounded-sm"
                      style={{ background: `${l.color}15`, color: l.color, border: `1px solid ${l.color}40` }}>
                      {l.code}
                    </span>
                    <GraduationCap size={16} className="text-muted" />
                  </div>
                )}
                <h3 className={`font-display font-bold text-lg ${l.cover_image ? "" : "mt-3"} leading-snug`}>{l.name}</h3>
                <p className="text-sm text-muted mt-2 line-clamp-2">{l.tagline || l.overview}</p>
              </div>
            </CoeLabCardLink>
          ))}
        </div>
      )}
    </div>
  );
}
